import { StateBlock, StatusBadge } from "@/components/layout-2/ds";
import type { BadgeValue } from "@/types/dynamic-detail";
import { formatEventSpan, groupRepeatedEvents } from "@/utils/event-log-groups";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

export type EventLogChange = {
    field?: string;
    label: string;
    old_value: string;
    new_value: string;
    /** Same line as the motif. Absent, the row stays an old → new diff. */
    plain?: boolean;
};

export type EventLogItem = {
    id: number | string;
    date: string;
    badge: BadgeValue;
    author: string;
    message?: string;
    reason?: string;
    changes?: EventLogChange[];
    /** Consecutive entries sharing this key fold into one expandable row. */
    groupKey?: string;
};

const dateFormatter = new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" });

/**
 * Journal d'événements d'un record, du plus récent au plus ancien : quand, quoi, qui, pourquoi et
 * ce qui a bougé. Le ton de chaque événement est servi par le backend. Une suite d'entrées sur le
 * même élément (même `groupKey`) se replie sous la plus récente, qui reste visible.
 */
export function EventLog({ items, emptyMessage = "Aucun événement." }: { items: EventLogItem[]; emptyMessage?: string }) {
    if (items.length === 0) {
        return <StateBlock kind="empty" message={emptyMessage} />;
    }
    return (
        <ol className="divide-y divide-border">
            {groupRepeatedEvents(items).map(row =>
                row.kind === "group" ? (
                    <EventLogGroup key={`group-${row.items[0].id}`} items={row.items} />
                ) : (
                    <li key={row.item.id} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
                        <EventLogEntry item={row.item} />
                    </li>
                )
            )}
        </ol>
    );
}

function EventLogGroup({ items }: { items: EventLogItem[] }) {
    const [expanded, setExpanded] = useState(false);
    const [latest, ...earlier] = items;
    const Chevron = expanded ? ChevronDown : ChevronRight;
    return (
        <li className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
            <EventLogEntry item={latest} />
            <button
                type="button"
                className="flex w-fit items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                aria-expanded={expanded}
                onClick={() => setExpanded(value => !value)}
            >
                <Chevron className="h-4 w-4 flex-shrink-0" />
                <span>
                    {items.length} entrées similaires · {formatEventSpan(earlier[earlier.length - 1].date, latest.date)} ·{" "}
                    {expanded ? "masquer les précédentes" : `afficher les ${earlier.length} précédentes`}
                </span>
            </button>
            {expanded && (
                <ol className="mt-1 divide-y divide-border rounded-sm border bg-gray-50 px-2">
                    {earlier.map(item => (
                        <li key={item.id} className="flex flex-col gap-1 py-2">
                            <EventLogEntry item={item} />
                        </li>
                    ))}
                </ol>
            )}
        </li>
    );
}

function EventLogLine({ label, value }: { label: string; value: string }) {
    return (
        <p className="text-sm">
            <span className="text-xs text-gray-600">{label} : </span>
            <span className="whitespace-pre-wrap">{value}</span>
        </p>
    );
}

function EventLogEntry({ item }: { item: EventLogItem }) {
    return (
        <>
            <div className="flex flex-wrap items-center gap-2">
                <StatusBadge value={item.badge} />
                <span className="text-xs tabular-nums text-gray-500">{dateFormatter.format(new Date(item.date))}</span>
                <span className="text-xs text-gray-500">· {item.author}</span>
            </div>
            {item.message && <p className="text-sm text-black">{item.message}</p>}
            {item.reason && <EventLogLine label="Motif" value={item.reason} />}
            {(item.changes ?? [])
                .filter(change => change.plain)
                .map((change, index) => (
                    <EventLogLine key={`${change.field ?? change.label}-${index}`} label={change.label} value={change.new_value} />
                ))}
            {(item.changes ?? []).some(change => !change.plain) && (
                <ul className="mt-1 space-y-0.5 rounded-sm border bg-gray-50 p-2">
                    {(item.changes ?? [])
                        .filter(change => !change.plain)
                        .map((change, index) => (
                            <li key={`${change.field ?? change.label}-${index}`} className="text-sm">
                                <span className="text-xs text-gray-600">{change.label} : </span>
                                <span className="text-gray-500 line-through">{change.old_value || "—"}</span>
                                <span className="text-gray-500"> → </span>
                                <span>{change.new_value || "—"}</span>
                            </li>
                        ))}
                </ul>
            )}
        </>
    );
}
